import { ISubscription, ISubscriptionContract } from '@getgreenline/webhooks'
import { ILocation, IAdminExternalSource } from '@getgreenline/homi-shared'

export enum SubscriptionStatusFilter {
  All = 'all',
  Active = 'active',
  Inactive = 'inactive',
}

export enum SubscriptionSort {
  EventAsc = 'eventAsc',
  EventDesc = 'eventDesc',
  LocationAsc = 'locationAsc',
  IntegratorAsc = 'integratorAsc',
}

export interface SubscriptionFilters {
  status: SubscriptionStatusFilter
  /** undefined means "all event types" */
  eventType?: string
  /** undefined means "all locations" */
  locationId?: number
  /** undefined means "all integrators" */
  integratorId?: string
}

export interface FilterOption<T> {
  value: T
  label: string
}

/** `undefined` means "no filter applied", which is always valid. */
export const isOptionValuePresent = <T>(
  value: T | undefined,
  options: FilterOption<T>[],
): boolean => value === undefined || options.some((option) => option.value === value)

/**
 * `isActive` was added in @getgreenline/webhooks@1.1.11. There is no backfill, so
 * subscriptions stored before the field existed have no value persisted. Absent
 * must be read as active — never check `=== true`.
 */
export const isSubscriptionActive = (subscription: ISubscriptionContract): boolean =>
  subscription.companyData.isActive !== false

export const sortByName = <T>(items: T[], getName: (item: T) => string): T[] =>
  [...items].sort((a, b) => getName(a).localeCompare(getName(b)))

const getLocationNameFactory = (locations: ILocation[]) => (locationId: number | undefined) => {
  if (locationId === undefined) return ''
  return locations.find((location) => location.id === locationId)?.name || ''
}

const getIntegratorNameFactory =
  (externalSources: IAdminExternalSource[]) => (integratorId: string) => {
    const parsedIntegratorId = parseInt(integratorId)
    return externalSources.find((source) => source.id === parsedIntegratorId)?.name || ''
  }

/** Distinct event types present across the given subscriptions, sorted A-Z by label. */
export const getDistinctEventTypeOptions = (
  subscriptions: ISubscriptionContract[],
): FilterOption<string>[] => {
  const eventTypes = Array.from(new Set(subscriptions.map((s) => s.companyData.eventType)))
  return sortByName(
    eventTypes.map((eventType) => ({ value: eventType, label: eventType })),
    (option) => option.label,
  )
}

/** Distinct locations present across the given subscriptions, sorted A-Z by resolved name. */
export const getDistinctLocationOptions = (
  subscriptions: ISubscriptionContract[],
  locations: ILocation[],
): FilterOption<number>[] => {
  const getLocationName = getLocationNameFactory(locations)
  const locationIds = Array.from(
    new Set(
      subscriptions
        .map((s) => s.companyData.locationId)
        .filter((id): id is number => id !== undefined),
    ),
  )
  return sortByName(
    locationIds.map((id) => ({ value: id, label: getLocationName(id) || String(id) })),
    (option) => option.label,
  )
}

/** Distinct integrators present across the given subscriptions, sorted A-Z by resolved name. */
export const getDistinctIntegratorOptions = (
  subscriptions: ISubscriptionContract[],
  externalSources: IAdminExternalSource[],
): FilterOption<string>[] => {
  const getIntegratorName = getIntegratorNameFactory(externalSources)
  const integratorIds = Array.from(new Set(subscriptions.map((s) => s.companyData.integratorId)))
  return sortByName(
    integratorIds.map((id) => ({ value: id, label: getIntegratorName(id) || id })),
    (option) => option.label,
  )
}

export const filterAndSortSubscriptions = (
  subscriptions: ISubscriptionContract[],
  filters: SubscriptionFilters,
  sort: SubscriptionSort,
  locations: ILocation[],
  externalSources: IAdminExternalSource[],
): ISubscriptionContract[] => {
  const getLocationName = getLocationNameFactory(locations)
  const getIntegratorName = getIntegratorNameFactory(externalSources)

  const filtered = subscriptions.filter((subscription) => {
    if (filters.status !== SubscriptionStatusFilter.All) {
      const active = isSubscriptionActive(subscription)
      if (filters.status === SubscriptionStatusFilter.Active ? !active : active) return false
    }
    if (
      filters.eventType !== undefined &&
      subscription.companyData.eventType !== filters.eventType
    ) {
      return false
    }
    if (
      filters.locationId !== undefined &&
      subscription.companyData.locationId !== filters.locationId
    ) {
      return false
    }
    if (
      filters.integratorId !== undefined &&
      subscription.companyData.integratorId !== filters.integratorId
    ) {
      return false
    }
    return true
  })

  switch (sort) {
    case SubscriptionSort.EventAsc:
      return sortByName(filtered, (s) => s.companyData.eventType)
    case SubscriptionSort.EventDesc:
      return sortByName(filtered, (s) => s.companyData.eventType).reverse()
    case SubscriptionSort.LocationAsc:
      return sortByName(filtered, (s) => getLocationName(s.companyData.locationId))
    case SubscriptionSort.IntegratorAsc:
      return sortByName(filtered, (s) => getIntegratorName(s.companyData.integratorId))
    default:
      return filtered
  }
}

interface BuildSubscriptionPayloadArgs {
  companyId: number
  eventType: string
  integratorId: string
  url: string
  locationId?: number
  integratorSecret?: string
  isActive?: boolean
  /** true when editing an existing subscription; create must never send isActive */
  isEdit: boolean
}

export const buildSubscriptionPayload = (args: BuildSubscriptionPayloadArgs): ISubscription => {
  const payload: ISubscription = {
    companyId: args.companyId,
    eventType: args.eventType,
    integratorId: args.integratorId,
    url: args.url,
  }

  if (args.locationId) {
    payload.locationId = args.locationId
  }
  if (args.integratorSecret) {
    payload.integratorSecret = args.integratorSecret
  }
  if (args.isEdit) {
    payload.isActive = args.isActive !== false
  }

  return payload
}
